import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { fetchCurrentUser } from '../modules/users';
import {
  buildPermissionSet,
  checkPermission,
  checkAnyPermission,
  checkAllPermissions,
} from '../lib/permissions';

const UserContext = createContext(null);
const ROLE_CHANGE_BASELINE_KEY = 'roleChangeBaseline';

const normalizePermissions = (permissions = []) =>
  [...new Set(permissions.filter(Boolean))];

const getUserRole = (user) => {
  const roleValue =
    user?.role ??
    user?.orgRole ??
    user?.organizationRole ??
    user?.organization_role ??
    user?.organization?.role ??
    null;

  return typeof roleValue === 'object'
    ? (roleValue?.name ?? roleValue?.key ?? roleValue?.slug ?? roleValue?.id ?? null)
    : roleValue;
};

const getUserRoleId = (user) =>
  user?.role_id ??
  user?.roleId ??
  user?.role?.id ??
  user?.organizationRole?.id ??
  null;

const getUserPermissions = (user, permissions = []) =>
  normalizePermissions(
    permissions.length
      ? permissions
      : user?.permissions ??
        user?.role?.permissions ??
        user?.organizationRole?.permissions ??
        user?.organization?.permissions ??
        []
  );

const buildRoleChangeSnapshot = (user, permissions) => ({
  userId: user?.id ?? null,
  organizationId: user?.organization_id ?? user?.organization?.id ?? null,
  role: getUserRole(user),
  roleId: getUserRoleId(user),
  permissions: getUserPermissions(user, permissions).sort(),
});

const readStoredBaseline = () => {
  try {
    const value = localStorage.getItem(ROLE_CHANGE_BASELINE_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const writeStoredBaseline = (snapshot) => {
  try {
    localStorage.setItem(ROLE_CHANGE_BASELINE_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore storage errors
  }
};

const hasRoleChange = (previous, next) => {
  if (!previous || !next) return false;

  if (
    previous.userId !== next.userId ||
    previous.organizationId !== next.organizationId
  ) {
    return false;
  }

  if (previous.role !== next.role) return true;
  if (previous.roleId !== next.roleId) return true;

  const previousPermissions = previous.permissions ?? [];
  const nextPermissions = next.permissions ?? [];

  return (
    previousPermissions.length !== nextPermissions.length ||
    nextPermissions.some((permission) => !previousPermissions.includes(permission))
  );
};

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('token')
  );
  const [roleChanged, setRoleChanged] = useState(false);
  const roleChangeBaselineRef = useRef(readStoredBaseline());
  const isPollingRef = useRef(false);

  const syncRoleChangeBaseline = useCallback((user, permissions = []) => {
    const snapshot = buildRoleChangeSnapshot(user, permissions);
    roleChangeBaselineRef.current = snapshot;
    writeStoredBaseline(snapshot);
  }, []);

  const applyUserSession = useCallback((user, org = null, permissions = []) => {
    setCurrentUser(user);
    setRole(getUserRole(user));
    setOrganization(org);
    setUserPermissions(getUserPermissions(user, permissions));
    syncRoleChangeBaseline(user, permissions);
  }, [syncRoleChangeBaseline]);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setHasLoaded(true);
      return null;
    }

    setIsLoading(true);
    try {
      const user = await fetchCurrentUser();
      if (user) {
        const incomingSnapshot = buildRoleChangeSnapshot(user, user?.permissions ?? []);
        if (hasRoleChange(roleChangeBaselineRef.current, incomingSnapshot)) {
          setRoleChanged(true);
        }
        applyUserSession(user, user?.organization ?? null, user?.permissions ?? []);
      }
      setHasLoaded(true);
      return user;
    } catch (err) {
      setHasLoaded(true);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [applyUserSession]);

  useEffect(() => {
    if (!hasLoaded) {
      refreshUser().catch(console.error);
    }
  }, [hasLoaded, refreshUser]);

  useEffect(() => {
    if (!isAuthenticated || !hasLoaded) return;

    const POLL_INTERVAL = 5 * 60 * 1000;

    const poll = async () => {
      if (document.hidden) return;
      if (isPollingRef.current) return;

      isPollingRef.current = true;
      try {
        const user = await fetchCurrentUser();
        if (!user) return;

        const incomingSnapshot = buildRoleChangeSnapshot(user, user?.permissions ?? []);
        if (hasRoleChange(roleChangeBaselineRef.current, incomingSnapshot)) {
          setRoleChanged(true);
          applyUserSession(user, user?.organization ?? null, user?.permissions ?? []);
        }
      } catch {
        // silently ignore poll errors
      } finally {
        isPollingRef.current = false;
      }
    };

    // const handleVisibilityChange = () => {
    //   if (!document.hidden) poll();
    // };

    const id = setInterval(poll, POLL_INTERVAL);
    // window.addEventListener('focus', poll);
    // document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(id);
      // window.removeEventListener('focus', poll);
      // document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, hasLoaded, applyUserSession]);

  const login = useCallback((data) => {
    if (data?.auth_token) {
      localStorage.setItem('token', `Bearer ${data.auth_token}`);
    }
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.removeItem(ROLE_CHANGE_BASELINE_KEY);
    roleChangeBaselineRef.current = null;
    setIsAuthenticated(true);
    setHasLoaded(false);
  }, []);

  const clearUserData = useCallback(() => {
    setCurrentUser(null);
    setRole(null);
    setOrganization(null);
    setUserPermissions([]);
    setIsAuthenticated(false);
    roleChangeBaselineRef.current = null;
    localStorage.removeItem(ROLE_CHANGE_BASELINE_KEY);
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    setHasLoaded(true);
  }, []);

  const updateUserData = useCallback((updatedUser) => {
    if (!updatedUser) return;
    setCurrentUser(updatedUser);
    setRole(getUserRole(updatedUser));
    setUserPermissions(getUserPermissions(updatedUser));
    syncRoleChangeBaseline(updatedUser, updatedUser?.permissions ?? []);
  }, [syncRoleChangeBaseline]);

  const updateOrganizationData = useCallback((updatedOrg) => {
    setOrganization(updatedOrg);
  }, []);

  const clearRoleChanged = useCallback(() => {
    setRoleChanged(false);
  }, []);

  const permissionSet = useMemo(
    () => buildPermissionSet(userPermissions),
    [userPermissions]
  );

  const can = useCallback(
    (permission) => checkPermission(permissionSet, permission),
    [permissionSet]
  );

  const hasAny = useCallback(
    (permissions) => checkAnyPermission(permissionSet, permissions),
    [permissionSet]
  );

  const hasAll = useCallback(
    (permissions) => checkAllPermissions(permissionSet, permissions),
    [permissionSet]
  );

  const value = useMemo(() => ({
    currentUser,
    role,
    organization,
    userPermissions,
    permissionSet,
    can,
    hasAny,
    hasAll,
    isLoading,
    hasLoaded,
    isAuthenticated,
    isReady: hasLoaded && !isLoading,
    roleChanged,
    clearRoleChanged,
    refreshUser,
    updateUserData,
    updateOrganizationData,
    clearUserData,
    login,
  }), [
    currentUser, role, organization, userPermissions, permissionSet,
    can, hasAny, hasAll, isLoading, hasLoaded, isAuthenticated,
    roleChanged, clearRoleChanged,
    refreshUser, updateUserData, updateOrganizationData, clearUserData, login,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};